/* ═══════════════════════════════════════════════════════════════
   VariantChangeService — the master → variant update feed (Phase 3).

   Reads ride the ordinary cloudCache list (the rows are firm-scoped and
   gated on specs.view like the spec itself). Writes never do: a decision
   has to stamp the feed row AND rewrite that client's layer together, so
   it goes through the server's bespoke actions, which hold
   `specs.accept_updates` and do both in one transaction.

   The maths — what a decision MEANS — lives in shared/variant-updates.ts
   and runs on both sides, so the panel can update its own copy the
   instant the server says yes.
   ═══════════════════════════════════════════════════════════════ */

import type { VariantChangeEntry } from '../../shared/variant-updates';
import * as cache from './cloudCache';
import type { BatchWrite, TableOpts } from './cloudCache';
import * as api from './apiClient';

const TABLE = 'spec_variant_changes';

const opts: TableOpts = { jsonFields: ['changes'] };

export const VariantChangeService = {
  /** Every feed entry for this firm. Cached like any other table. */
  list: async (): Promise<VariantChangeEntry[]> => cache.list<VariantChangeEntry>(TABLE, opts),

  /** This master's entries, newest publish first. */
  listForMaster: async (masterId: string): Promise<VariantChangeEntry[]> => {
    const all = await VariantChangeService.list();
    return all
      .filter((e) => e.specMasterId === masterId)
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  },

  /** Raised by the publish path — one entry per variant the publish touched. */
  create: async (entries: VariantChangeEntry[]): Promise<void> => {
    if (!entries.length) return;
    await api.variantChangesCreate(entries);
    cache.invalidate(TABLE);
  },

  /** The same raise as a batch op — rides a publish's single transaction.
   *  Entries may arrive already DECIDED (the publish dialog's answers); the
   *  server derives each row's status from its changes. */
  createWrite: (entries: VariantChangeEntry[]): BatchWrite => ({
    kind: 'op',
    op: { action: 'variant_changes_create', entries },
    tables: [TABLE],
  }),

  /** Accept or reject ONE change. The server also rewrites the client's
   *  layer, so the master's cache entry is dropped too. */
  decide: async (
    entryId: string,
    changeId: string,
    decision: 'accepted' | 'rejected'
  ): Promise<VariantChangeEntry | null> => {
    const res = await api.variantChangeDecide({ entryId, changeId, decision });
    cache.invalidate(TABLE);
    cache.invalidate('spec_masters');
    return res?.entry ? (cache.rowToCamel(res.entry, opts) as VariantChangeEntry) : null;
  },

  /** Accept all / Reject all — every still-pending change in one entry. */
  decideAll: async (
    entryId: string,
    decision: 'accepted' | 'rejected'
  ): Promise<VariantChangeEntry | null> => {
    const res = await api.variantChangeDecideAll({ entryId, decision });
    cache.invalidate(TABLE);
    cache.invalidate('spec_masters');
    return res?.entry ? (cache.rowToCamel(res.entry, opts) as VariantChangeEntry) : null;
  },
};
